import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';

import { Button, Divider } from '@ds/core';
import { Alert } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { MarcaDoGoogle } from '@/comuns/MarcaDoGoogle';
import { criarConta, entrarComGoogle, googleDisponivel } from '@/session/sessao';

/**
 * Passo 1: a conta. Google, quando ligado no servidor, ou e-mail e senha — que
 * esperam a confirmação pelo link, e o link volta para `destino`.
 */
export function EtapaConta({ destino, aoCriar }: {
    destino: string;
    aoCriar: (email: string, precisaConfirmar: boolean) => void;
}) {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [criando, setCriando] = useState(false);
    const [comGoogle, setComGoogle] = useState(false);
    const [indoAoGoogle, setIndoAoGoogle] = useState(false);

    const idEmail = useId();
    const idSenha = useId();
    const idConfirmacao = useId();

    useEffect(() => {
        let vivo = true;
        void googleDisponivel().then((sim) => { if (vivo) setComGoogle(sim); });
        return () => { vivo = false; };
    }, []);

    const enviar = async () => {
        setErro(null);
        /* A conferência é aqui porque o servidor não a faz: ele guarda a senha
           que receber, inclusive a que foi digitada errado duas vezes. */
        if (senha.length < 8) {
            setErro('A senha precisa de pelo menos 8 caracteres.');
            return;
        }
        if (senha !== confirmacao) {
            setErro('As duas senhas não são iguais.');
            return;
        }
        setCriando(true);
        const r = await criarConta({ email, senha, destino });
        setCriando(false);
        if (!r.ok) {
            setErro(r.erro);
            return;
        }
        aoCriar(email.trim().toLowerCase(), r.precisaConfirmar);
    };

    const irAoGoogle = async () => {
        setErro(null);
        setIndoAoGoogle(true);
        const r = await entrarComGoogle(destino);
        if (!r.ok) {
            setIndoAoGoogle(false);
            setErro(r.erro ?? 'Não foi possível abrir o Google.');
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); void enviar(); }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {comGoogle && (
                <>
                    <Button type="button" variant="secondary" fullWidth loading={indoAoGoogle} iconLeft={<MarcaDoGoogle />} onClick={() => { void irAoGoogle(); }}>
                        Continuar com Google
                    </Button>
                    <Divider label="ou" spacing={0} />
                </>
            )}

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            <FormField label="E-mail" htmlFor={idEmail} required>
                <Input id={idEmail} type="email" name="email" autoComplete="username" value={email} onChange={setEmail} placeholder="voce@suaclinica.com.br" />
            </FormField>

            <FormField label="Senha" htmlFor={idSenha} hint="Pelo menos 8 caracteres." required>
                <Input id={idSenha} type="password" name="senha" autoComplete="new-password" value={senha} onChange={setSenha} />
            </FormField>

            <FormField label="Repita a senha" htmlFor={idConfirmacao} required>
                <Input id={idConfirmacao} type="password" name="confirmacao" autoComplete="new-password" value={confirmacao} onChange={setConfirmacao} />
            </FormField>

            <Button type="submit" fullWidth loading={criando}>Continuar</Button>

            <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                Foi convidado por uma clínica? Use o <strong>mesmo e-mail do convite</strong>: você entra direto na equipe, sem plano nem pagamento.
            </p>

            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', textAlign: 'center' }}>
                Já tem conta? <Link to="/entrar" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>Entrar</Link>
            </p>
        </form>
    );
}
